import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { BookOpenCheck, Check, ChevronRight, Flame, GraduationCap, Pencil, Plus, Sparkles, Target, Trash2, X } from "lucide-react";
import { Button, Card, EmptyState, PageHeader } from "../components/ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import type { LearningPlan } from "../types";

const PRESETS = [
  { title: "剪映剪辑 21 天", days: 21, minutes: 30, hint: "从界面到完整作品" },
  { title: "外贸英语 30 天", days: 30, minutes: 20, hint: "行业真实场景" },
  { title: "社媒运营 21 天", days: 21, minutes: 30, hint: "账号从 0 到 1" },
  { title: "摄影构图 14 天", days: 14, minutes: 25, hint: "手机也能拍好" }
];

function ringStyle(percent: number): React.CSSProperties {
  return {
    background: `conic-gradient(var(--ops-accent) ${percent * 3.6}deg, var(--ops-surface-quiet) 0deg)`
  };
}

export function PlansPage() {
  const { data, dataRevision, mutate } = useApp();
  const [plans, setPlans] = useState<LearningPlan[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState<boolean | string>(false);
  const [editing, setEditing] = useState<LearningPlan | null>(null);
  const [completing, setCompleting] = useState<LearningPlan | null>(null);
  const [reflection, setReflection] = useState("");
  const [justGained, setJustGained] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const requestSequence = useRef(0);

  const refresh = useCallback(async () => {
    const sequence = ++requestSequence.current;
    try {
      const result = await api.listLearningPlans();
      if (sequence !== requestSequence.current) return;
      setPlans(result);
      setLoaded(true);
      setLoadError(null);
    } catch (error) {
      if (sequence === requestSequence.current) setLoadError(error instanceof Error ? error.message : "学习计划读取失败");
    }
  }, []);
  useEffect(() => {
    void refresh();
    return () => { requestSequence.current += 1; };
  }, [dataRevision, refresh]);

  const save = async <T,>(action: () => Promise<T>, message: string) => {
    if (pending.current) return null;
    pending.current = true;
    setSaving(true);
    try { return await mutate(action, message); }
    finally { pending.current = false; setSaving(false); }
  };

  const submitPlan = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const skillId = String(form.get("skillId") ?? "");
    const result = await save(
      () =>
        api.createLearningPlan({
          title: String(form.get("title") ?? ""),
          skillId: skillId || undefined,
          goal: String(form.get("goal") ?? ""),
          totalDays: Number(form.get("totalDays") ?? 21),
          dailyMinutes: Number(form.get("dailyMinutes") ?? 30)
        }),
      "学习计划已建立，加油！"
    );
    if (result === null) return;
    setCreating(false);
    await refresh();
  };

  const submitEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    const form = new FormData(event.currentTarget);
    const skillId = String(form.get("skillId") ?? "");
    const result = await save(
      () =>
        api.updateLearningPlan(editing.id, {
          title: String(form.get("title") ?? ""),
          skillId: skillId || null,
          goal: String(form.get("goal") ?? ""),
          totalDays: Number(form.get("totalDays") ?? editing.total_days),
          dailyMinutes: Number(form.get("dailyMinutes") ?? editing.daily_minutes)
        }),
      "计划已更新"
    );
    if (result === null) return;
    setEditing(null);
    await refresh();
  };

  const removePlan = async (plan: LearningPlan) => {
    const result = await save(() => api.deleteLearningPlan(plan.id), "计划已删除");
    if (result === null) return;
    await refresh();
  };

  const completeDay = async (plan: LearningPlan) => {
    if (!plan.currentDay) return;
    const result = await save(
      () => api.completeLearningDay(plan.currentDay!.id, reflection.trim()),
      "打卡成功"
    );
    if (result === null) return;
    setJustGained(result.xp > 0 ? result.xp : null);
    setCompleting(null);
    setReflection("");
    setTimeout(() => setJustGained(null), 3000);
    await refresh();
  };

  return (
    <div className="page page--plans">
      <PageHeader
        title="学习计划"
        description="把想成为的样子，拆成今天可以完成的一步。"
        actions={<Button icon={<Plus size={15} />} onClick={() => setCreating(true)}>新建计划</Button>}
      />

      {loadError && <div role="alert"><p>学习计划未能刷新：{loadError}。已有内容仍保留。</p><Button variant="secondary" onClick={() => void refresh()}>重新读取</Button></div>}
      {!loaded && !loadError && <p role="status">正在读取学习计划…</p>}

      <div className="compound-strip">
        <div className="compound-chip"><GraduationCap size={15} /><span>累计打卡 <b>{plans.reduce((sum, plan) => sum + plan.completed_days, 0)}</b> 天</span></div>
        <div className="compound-chip"><Flame size={15} /><span>进行中 <b>{plans.filter((plan) => plan.status === "ACTIVE").length}</b> 个计划</span></div>
        <div className="compound-chip"><Sparkles size={15} /><span>完成打卡沉淀事实，关联技能积累 XP</span></div>
      </div>

      {creating && (
        <Card className="plan-create">
          <h3><Target size={17} /> 新建学习计划</h3>
          <div className="preset-grid">
            {PRESETS.map((preset) => (
              <button key={preset.title} onClick={() => setCreating(preset.title)}>
                <strong>{preset.title}</strong><small>{preset.days} 天 · 每天 {preset.minutes} 分钟</small><span>{preset.hint}</span>
              </button>
            ))}
          </div>
          <form onSubmit={(event) => void submitPlan(event)} className="plan-form">
            <div className="field"><span>计划名称 *</span><input name="title" required defaultValue={typeof creating === "string" ? creating : ""} placeholder="如：剪映剪辑 21 天" /></div>
            <div className="field"><span>关联技能（打卡自动加 XP）</span><select name="skillId" defaultValue=""><option value="">不关联</option>{data?.skills.map((skill) => <option key={skill.id} value={skill.id}>{skill.title}（{skill.level_title}）</option>)}</select></div>
            <div className="field"><span>总天数</span><input name="totalDays" type="number" min={3} max={90} defaultValue={21} /></div>
            <div className="field"><span>每天时长（分钟）</span><input name="dailyMinutes" type="number" min={5} max={240} defaultValue={30} /></div>
            <div className="field field--full"><span>目标（可选）</span><input name="goal" placeholder="想达到什么水平？" /></div>
            <div className="modal__actions">
              <Button type="button" variant="secondary" disabled={saving} onClick={() => setCreating(false)}>取消</Button>
              <Button type="submit" disabled={saving}>{saving ? "正在保存…" : "建立计划"}</Button>
            </div>
          </form>
        </Card>
      )}

      {justGained !== null && (
        <div className="xp-toast">打卡成功！技能 XP +{justGained}</div>
      )}

      {plans.length === 0 && loaded && !loadError ? (
        <EmptyState icon={<BookOpenCheck size={24} />} title="还没有学习计划" description="选一个方向开始：剪映剪辑、外贸英语、社媒运营…" actionLabel="新建第一个计划" onAction={() => setCreating(true)} />
      ) : (
        <div className="plans-grid">
          {plans.map((plan) => {
            const percent = Math.round((plan.completed_days / plan.total_days) * 100);
            const day = plan.currentDay;
            return (
              <Card key={plan.id} className="plan-card">
                <div className="plan-card__head">
                  <div className="plan-ring" style={ringStyle(percent)}><span>{percent}%</span></div>
                  <div className="plan-card__title">
                    <h3>{plan.title}</h3>
                    <small>Day {Math.min(plan.completed_days + 1, plan.total_days)} / {plan.total_days} · {plan.daily_minutes} 分钟/天</small>
                    {plan.goal && <p>{plan.goal}</p>}
                  </div>
                  <div className="plan-card__ops">
                    <button disabled={saving} onClick={() => setEditing(plan)} title="编辑计划"><Pencil size={15} /></button>
                    <button disabled={saving} className="danger" onClick={() => void removePlan(plan)} title="删除计划"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="plan-progress"><i style={{ width: `${percent}%` }} /></div>
                <div className="plan-streak"><Flame size={13} /> 已完成 {plan.completed_days} / {plan.total_days} 天</div>
                {day ? (
                  <div className="plan-today">
                    <small>今日任务 · Day {day.day_no}</small>
                    <strong>{day.title}</strong>
                    <p>{day.description}</p>
                    <Button disabled={saving} size="sm" icon={<Check size={14} />} onClick={() => setCompleting(plan)}>完成打卡</Button>
                  </div>
                ) : (
                  <div className="plan-done">全部完成！这个计划毕业了</div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {completing?.currentDay && (
        <div className="modal-backdrop" onClick={() => setCompleting(null)}>
          <div className="modal-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="modal__header"><div><span className="eyebrow">DAILY CHECK-IN</span><h2>完成打卡 · Day {completing.currentDay.day_no}</h2><p>写一句今天实际学了什么（教练会看）。</p></div><button className="modal__close" aria-label="关闭" onClick={() => setCompleting(null)}><X size={18} /></button></div>
            <div className="modal__body">
              <label className="field"><span>今日学习内容（真实一点）</span><textarea rows={3} value={reflection} onChange={(event) => setReflection(event.target.value)} placeholder="例如：学会了剪映的画中画，做了 3 段素材叠加练习…" autoFocus /></label>
            </div>
            <div className="modal__footer"><Button disabled={saving} variant="ghost" onClick={() => setCompleting(null)}>取消</Button><Button disabled={saving} icon={<Check size={15} />} onClick={() => void completeDay(completing)}>{saving ? "正在保存…" : "完成打卡"}</Button></div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="modal__header"><div><span className="eyebrow">EDIT PLAN</span><h2>编辑计划</h2><p>修改后按新设置继续打卡。</p></div><button className="modal__close" aria-label="关闭" onClick={() => setEditing(null)}><X size={18} /></button></div>
            <form onSubmit={(event) => void submitEdit(event)} className="plan-form">
              <div className="modal__body">
                <div className="field field--full"><span>计划名称 *</span><input name="title" required defaultValue={editing.title} placeholder="如：剪映剪辑 21 天" /></div>
                <div className="field field--full"><span>关联技能（打卡自动加 XP）</span><select name="skillId" defaultValue={editing.skill_id ?? ""}><option value="">不关联</option>{data?.skills.map((skill) => <option key={skill.id} value={skill.id}>{skill.title}（{skill.level_title}）</option>)}</select></div>
                <div className="field"><span>总天数</span><input name="totalDays" type="number" min={Math.max(editing.completed_days, 3)} max={90} defaultValue={editing.total_days} /></div>
                <div className="field"><span>每天时长（分钟）</span><input name="dailyMinutes" type="number" min={5} max={240} defaultValue={editing.daily_minutes} /></div>
                <div className="field field--full"><span>目标（可选）</span><input name="goal" defaultValue={editing.goal} placeholder="想达到什么水平？" /></div>
              </div>
              <div className="modal__footer"><Button disabled={saving} type="button" variant="ghost" onClick={() => setEditing(null)}>取消</Button><Button disabled={saving} type="submit">{saving ? "正在保存…" : "保存修改"}</Button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
